<script setup>
import { computed } from "vue";
import { ArrowPathIcon } from "@heroicons/vue/24/outline";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import nQuerioLogoColor from "@/assets/img/n_color.svg";

const props = defineProps({
  variant: {
    type: String,
    default: "sky",
    validator: (value) => ["amber", "sky"].includes(value),
  },
  statusIcon: {
    type: [Object, Function],
    default: null,
  },
  statusLabel: { type: String, required: true },
  title: { type: String, required: true },
  message: { type: String, required: true },
  buttonLabel: { type: String, required: true },
});

defineEmits(["action"]);

const VARIANT_CLASSES = {
  amber: {
    card: "border-amber-200/80 bg-linear-to-br from-amber-50 via-white to-sky-50 dark:border-white/10 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
    blurTop: "bg-amber-300/30 dark:bg-amber-400/10",
    blurBottom: "bg-sky-300/30 dark:bg-sky-400/10",
    pill: "border-amber-300/80 bg-white/90 text-amber-800 dark:border-amber-200/10 dark:bg-white/5 dark:text-amber-200",
    dot: "bg-amber-500 shadow-[0_0_0_4px_rgba(245,158,11,0.18)]",
  },
  sky: {
    card: "border-sky-200/80 bg-linear-to-br from-sky-50 via-white to-indigo-50 dark:border-white/10 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
    blurTop: "bg-sky-300/30 dark:bg-sky-400/10",
    blurBottom: "bg-indigo-300/30 dark:bg-indigo-400/10",
    pill: "border-sky-300/80 bg-white/90 text-sky-800 dark:border-sky-200/10 dark:bg-white/5 dark:text-sky-200",
    dot: "bg-sky-500 shadow-[0_0_0_4px_rgba(14,165,233,0.18)]",
  },
};

const classes = computed(() => VARIANT_CLASSES[props.variant]);
</script>

<template>
  <section class="w-full max-w-3xl mx-auto">
    <div
      :class="classes.card"
      class="relative overflow-hidden rounded-4xl border px-6 py-8 shadow-[0_24px_80px_rgba(15,23,42,0.12)] sm:px-10 sm:py-12"
    >
      <div
        :class="classes.blurTop"
        class="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full blur-3xl"
      />
      <div
        :class="classes.blurBottom"
        class="pointer-events-none absolute -bottom-12 -left-10 h-36 w-36 rounded-full blur-3xl"
      />

      <div class="relative">
        <div
          :class="classes.pill"
          class="mb-8 inline-flex items-center gap-3 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm"
        >
          <component
            :is="statusIcon"
            v-if="statusIcon"
            class="size-5"
            aria-hidden="true"
          />
          <span
            v-else
            :class="classes.dot"
            class="inline-flex h-2.5 w-2.5 rounded-full"
          />
          {{ statusLabel }}
        </div>

        <div
          class="flex flex-col gap-8 md:flex-row md:items-start md:justify-between"
        >
          <div class="max-w-xl">
            <img
              :src="nQuerioLogoColor"
              alt="nQuerio"
              class="mb-6 h-10 w-auto"
            />
            <h1
              class="mb-4 text-4xl font-black tracking-tight text-slate-900 dark:text-white"
            >
              {{ title }}
            </h1>
            <p
              class="max-w-lg text-lg leading-8 text-slate-700 dark:text-slate-300"
            >
              {{ message }}
            </p>
          </div>

          <div
            class="rounded-2xl border border-slate-200/80 bg-white/80 p-4 backdrop-blur-sm dark:border-white/10 dark:bg-white/5"
          >
            <PrimaryButton
              :label="buttonLabel"
              class="inline-flex items-center gap-2 px-5 py-3 text-base"
              @click="$emit('action')"
            >
              <ArrowPathIcon class="size-5" aria-hidden="true" />
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
